import { supabase } from './supabase';

/**
 * QuickBooks invoice sync, Nexus side.
 *
 * The worker (service role) calls, in order:
 *   claim_quickbooks_sync(limit)
 *   complete_quickbooks_sync(id, qbo_id, sync_token, customer_qbo_id, customer_sync_token)
 *   fail_quickbooks_sync(id, error)
 *
 * claim returns events already sorted: deposits, invoices, payments, tips,
 * surcharges, refunds. Process that array in order. A deposit earlier in the
 * same batch is the QuickBooks id for deposits_to_apply on a later invoice.
 *
 * Payload version is 1. Store line.net_cents as the line amount. Tax
 * components are already rounded. Payments, deposits, tips, and surcharges
 * deposit to Undeposited Funds. A customer_deposit is a liability, applied
 * when the invoice is created, not received again. Tips go to Tips Payable.
 * Surcharges go to Surcharge Income. store_credit is wallet. refund_receipt
 * is money sent back. Do not recompute tax and do not post to the bank account.
 */

export type QuickBooksSyncStatus = 'pending' | 'in_progress' | 'synced' | 'error' | 'skipped';

export interface QuickBooksConnection {
  business_id: string;
  realm_id: string | null;
  home_currency: string | null;
  sync_enabled: boolean;
  connected_at: string | null;
}

export interface QuickBooksInvoiceSync {
  status: QuickBooksSyncStatus;
  error: string | null;
  syncedAt: string | null;
  operation: string;
}

export async function getQuickBooksConnection(businessId: string): Promise<QuickBooksConnection | null> {
  const { data, error } = await supabase
    .from('quickbooks_connections')
    .select('business_id, realm_id, home_currency, sync_enabled, connected_at')
    .eq('business_id', businessId)
    .maybeSingle();
  if (error) throw error;
  return data as QuickBooksConnection | null;
}

export async function saveQuickBooksConnection(params: {
  businessId: string;
  realmId?: string | null;
  homeCurrency?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('set_quickbooks_connection', {
    p_business_id: params.businessId,
    p_realm_id: params.realmId ?? null,
    p_home_currency: params.homeCurrency ?? null,
  });
  if (error) throw error;
}

export async function setQuickBooksSyncEnabled(businessId: string, enabled: boolean): Promise<void> {
  const { error } = await supabase.rpc('set_quickbooks_sync_enabled', {
    p_business_id: businessId,
    p_enabled: enabled,
  });
  if (error) throw error;
}

export function quickBooksSyncLabel(state: QuickBooksInvoiceSync | undefined): string | null {
  if (!state) return null;
  if (state.status === 'synced') return state.operation === 'void' || state.operation === 'delete' ? 'Removed from QuickBooks' : 'In QuickBooks';
  if (state.status === 'error') return 'QuickBooks needs attention';
  if (state.status === 'in_progress') return 'Sending to QuickBooks';
  if (state.status === 'pending') return 'Waiting for QuickBooks';
  return null;
}

/** Latest invoice event per id. Empty when sync is off, so the list stays quiet. */
export async function fetchQuickBooksInvoiceStatuses(
  businessId: string,
  invoiceIds: string[],
): Promise<Map<string, QuickBooksInvoiceSync>> {
  const out = new Map<string, QuickBooksInvoiceSync>();
  if (!invoiceIds.length) return out;
  const connection = await getQuickBooksConnection(businessId);
  if (!connection?.sync_enabled) return out;

  const { data, error } = await supabase
    .from('accounting_sync_outbox')
    .select('entity_id, status, error, operation, synced_at, updated_at')
    .eq('business_id', businessId)
    .eq('provider', 'quickbooks')
    .eq('entity_type', 'invoice')
    .in('entity_id', invoiceIds)
    .order('updated_at', { ascending: false });
  if (error) throw error;

  for (const row of data || []) {
    const id = row.entity_id as string;
    if (out.has(id)) continue;
    out.set(id, {
      status: row.status as QuickBooksSyncStatus,
      error: (row.error as string | null) ?? null,
      syncedAt: (row.synced_at as string | null) ?? null,
      operation: row.operation as string,
    });
  }
  return out;
}
